import { ComponentStore, OnStoreInit, tapResponse } from '@ngrx/component-store';
import { catchError, exhaustMap, first, of, pipe, takeUntil, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { handleError } from '../../../shared/utils/handle-error';
import { Board, BoardsApiService, DashboardBoard, DefaultProjectAccess } from '../../../shared/services/api/boards-api.service';
import { InputDialogService } from '../../../shared/components/input-dialog';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Utils } from '../../../shared/utils/utils';
import { SelectedWorkspaceStore } from '../../../stores/selected-workspace.store';
import { Router } from '@angular/router';
import { WorkspaceStore } from './workspace-companies.store';
import { TranslateService } from '@ngx-translate/core';

export type WorkspaceBoardsStoreState = {
  isLoading: boolean;
  isInited: boolean;
  currentProjectId: string;
  boards: Board[];
};

@Injectable()
export class WorkspaceBoardsStore
  extends ComponentStore<WorkspaceBoardsStoreState>
  implements OnStoreInit
{
  public stateVm$ = this.select((state) => state, { debounce: true });
  private isMobileMenu = Utils.isMobile();
  public boards$ = this.select((state) => state.boards);

  constructor(
    private boardsApi: BoardsApiService,
    private message: NzMessageService,
    private inputDialog: InputDialogService,
    private selectedWorkspace: SelectedWorkspaceStore,
    private notify: NzMessageService,
    private router: Router,
    private workspaceStore: WorkspaceStore,
    private translate: TranslateService,
  ) {
    super({
      currentProjectId: '',
      isInited: false,
      isLoading: true,
      boards: [],
    });
  }

  ngrxOnStoreInit() {
    this.init();
  }

  public init(force = false) {
    this.selectedWorkspace.project$.pipe(takeUntil(this.destroy$)).subscribe((it) => {
      const currentState = this.get();
      if (it && it.id !== currentState.currentProjectId || it && force) {
        this.getProjectBoards(it.id);
      }
    });
  }

  private getProjectBoards = this.effect<string>((projectId$) => {
    return projectId$.pipe(
      tap((id) => {
        this.patchState({ isLoading: true, currentProjectId: id, boards: [], isInited: false });
      }),
      exhaustMap((id) => {
        return this.boardsApi.getProjectBoardsList(id).pipe(
          catchError(() => of([])),
          tapResponse(
            (items) => {
              const normalizedBoards: Board[] = (items || []).map((item: any) => {
                // Normalize users array
                const users = Array.isArray(item.users) ? item.users : [];

                // Check itemType to determine board type
                if (item.itemType === 'dashboard') {
                  // Convert dashboard item to DashboardBoard format
                  return {
                    type: 'dashboard' as const,
                    id: item.id || item.uuid,
                    name: item.name || item.title || '',
                    authorId: item.authorId || 0,
                    projectId: item.projectId,
                    createdAt: item.createdAt,
                    project_access: item.project_access || JSON.parse(JSON.stringify(DefaultProjectAccess)),
                    rawData: {
                      reportId: item.plotUuid ? undefined : undefined,
                      reportType: 'valuation' as const,
                      reportData: null,
                    },
                    users,
                    sharedLink: {
                      accessLevel: item.sharedLink?.accessLevel || item.accessLevel || null,
                      id: item.sharedLink?.id || null,
                    },
                  } as DashboardBoard;
                } else {
                  // Regular board (map type) - preserve all original fields
                  return {
                    ...item,
                    type: (item.type || 'map') as 'map',
                    users,
                  } as Board;
                }
              });

              // Sort by date created (newest first)
              normalizedBoards.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

              this.patchState({ boards: normalizedBoards, isLoading: false, isInited: true });
            },
            (err) => {
              this.patchState({ isLoading: false, currentProjectId: '', boards: [], isInited: false });
              this.handleError(new Error('Error while loading boards list'));
            },
          ),
        );
      }),
    );
  });

  public refreshProjectBoards = this.effect<void>(
    pipe(
      tap(() => {
        const projectId = this.get().currentProjectId;

        if (projectId) {
          this.getProjectBoards(projectId);
        }
      }),
    ),
  );

  public onBoardUpdated(board: Board) {
    const state = this.get();

    const boards = state.boards;

    this.patchState({
      boards: boards.map((it) => {
        if (it.id !== board.id) {
          return it;
        }

        return board;
      }),
    });
  }

  public clear() {
    this.patchState({
      isLoading: true,
      currentProjectId: '',
      isInited: false,
      boards: [],
    });
  }

  // public createCompany = this.effect<CompanyCreatePayload>((args$) => {
  //   return args$.pipe(
  //     exhaustMap((args) => {
  //       return this.companyService.createCompany(args).pipe(
  //         tapResponse(
  //           (boar) => {
  //             this.patchState({ boards: [...this.get().companies, company] });
  //           },
  //           () => {
  //             this.handleError(new Error('Error while creating company'));
  //           },
  //         ),
  //       );
  //     }),
  //   );
  // });

  private handleError(error: Error) {
    handleError(error, { source: 'workspace-boards', operation: 'boards-management' });
  }

  public editBoard = this.effect<Board>((board$) => {
    return board$.pipe(
      tap((board) => {
        const user = this.selectedWorkspace.getUserSync();

        if (!user) {
          return;
        }

        if (user.id !== board.authorId) {
          return;
        }

        this.inputDialog.open({
          title: 'Rename case',
          label: this.translate.instant('enter_case_name'),
          placeholder: this.translate.instant('untitled'),
          initialValue: board.name,
          primaryButton: { text: this.translate.instant('ok'), style: 'filled-black' },
          secondaryButton: { text: this.translate.instant('cancel'), style: 'outlined-black' },
        }).pipe(first()).subscribe((result) => {
          if (!result || !result.confirmed || !result.value) {
            return;
          }

          const updatedBoard: Board = {
            ...board,
            name: result.value,
          };

          this.boardsApi
            .updateBoard(updatedBoard)
            .pipe(first())
            .subscribe(() => {
              this.onBoardUpdated(updatedBoard);
              this.message.success(this.translate.instant('board_updated') || 'Board updated');
            });
        });
      }),
      catchError((err: any) => {
        handleError(err, { source: 'workspace-boards', operation: 'delete-board' });
        return of([]);
      }),
    );
  });

  public createBoard = this.effect<boolean>((args$) => {
    return args$.pipe(
      exhaustMap((navigateToBoardAfterCreation) => {
        return this.boardsApi
          .createProjectBoard(this.selectedWorkspace.getStateSync().workspace?.id || '', {
            name: 'Board',
          })
          .pipe(
            tapResponse(
              (res) => {
                this.notify.success('New board created');

                if (navigateToBoardAfterCreation) {
                  // @ts-ignore
                  this.router.navigate(['board', res.boardId]).then(() => window.location.reload());
                }

                this.init(true);
              },
              (e: Error) => {
                this.handleError(e);
                this.translate.get('BOARDS.ERROR_CREATING').subscribe(text => {
                  this.notify.error(text);
                });
              },
            ),
          );
      }),
    );
  });

  public createBoardWithName(boardName: string) {
    this.boardsApi
      .createProjectBoard(this.selectedWorkspace.getStateSync().workspace?.id || '', {
        name: boardName,
      })
      .subscribe({
        next: (res) => {
          this.notify.success('New case created');
          this.init(true);
        },
        error: (e: Error) => {
          this.handleError(e);
          this.notify.error(this.translate.instant('failed_to_create_case'));
        },
      });
  }

  public deleteBoard = this.effect<Board>((args$) => {
    return args$.pipe(
      exhaustMap((board: Board) => {
        return this.boardsApi.deleteBoard(board).pipe(
          tapResponse(
            (res) => {
              this.patchState({
                boards: this.get().boards.filter((it) => it.id !== board.id),
              });
            },
            (e: Error) => {
              this.handleError(e);
            },
          ),
        );
      }),
    );
  });
}
